import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";
import PropTypes from "prop-types";
import cs from "classnames";

import Loading from "components/Loading";
import CustomTable from "components/CustomTable";

RevisionTable.propTypes = {
	title: PropTypes.string,
	about: PropTypes.string,
	revisions: PropTypes.array,
	defaultPageSize: PropTypes.number,
	setEditing: PropTypes.func,
	displayActions: PropTypes.bool,
};

export default function RevisionTable({
	title = "Seznam všech revizí",
	about,
	revisions = null,
	defaultPageSize = 30,
	setEditing,
	displayActions = true,
}) {
	const { users, items } = useSelector(state => state.data);

	const [data, setData] = useState({ nodes: [] });
	useEffect(() => {
		if (revisions === null) return;

		const newData = revisions.map(revision => {
			const newItems = revision.items.map(item => items?.[item]);
			return { ...revision, doneBy: users?.[revision.doneBy], items: newItems };
		});
		setData({
			nodes: newData.sort((a, b) =>
				moment(a.next, "YYYY-MM-DDTHH:mm:ssZ").isBefore(moment(b.next, "YYYY-MM-DDTHH:mm:ssZ"))
					? -1
					: 1
			),
		});
	}, [revisions, users, items]);

	const columns = [
		{
			label: "Provedl",
			search: revision => revision.doneBy?.username || "-",
			renderCell: revision => (
				<Link to={`/uzivatele/${revision.doneBy?.id || ""}`}>
					{revision.doneBy?.username || "-"}
				</Link>
			),
			sort: { sortKey: "DONEBY" },
		},
		{
			label: "Datum",
			renderCell: revision => revision.date && moment(revision.date).format("D. M. YYYY"),
			sort: { sortKey: "DATE" },
		},
		{
			label: "Následující",
			search: revision => revision.next && moment(revision.next).format("D. M. YYYY"),
			renderCell: revision => (
				<span className={cs({ red: moment(revision?.next).isBefore(moment()) })}>
					{revision.next && moment(revision.next).format("D. M. YYYY")}
				</span>
			),
			sort: { sortKey: "NEXT" },
		},
		{
			label: "Předměty",
			search: revision => revision.items.map(item => item?.label || "-").join(", "),
			renderCell: revision =>
				revision.items.map((item, key) => (
					<React.Fragment key={key}>
						{key === 0 ? "" : ", "}
						<Link to={`/predmety/${item?.id || ""}`}>{item?.label || "-"}</Link>
					</React.Fragment>
				)),
			sort: { sortKey: "ITEMS" },
		},
	];

	const sortFns = {
		DONEBY: array => array.sort((a, b) => a.doneBy.username.localeCompare(b.doneBy.username)),
		DATE: array =>
			array.sort((a, b) =>
				moment(a.date, "YYYY-MM-DDTHH:mm:ssZ").isBefore(moment(b.date, "YYYY-MM-DDTHH:mm:ssZ"))
					? -1
					: 1
			),
		NEXT: array =>
			array.sort((a, b) =>
				moment(a.next, "YYYY-MM-DDTHH:mm:ssZ").isBefore(moment(b.next, "YYYY-MM-DDTHH:mm:ssZ"))
					? -1
					: 1
			),
		ITEMS: array =>
			array.sort((a, b) =>
				a.items
					.map(item => item.label)
					.join(", ")
					.localeCompare(b.items.map(item => item.label).join(", "))
			),
	};

	if (revisions === null) return <Loading />;

	return (
		<CustomTable
			title={title}
			about={about}
			layout="minmax(6rem, 1fr) minmax(9rem, 1fr) minmax(9rem, 1fr) minmax(24rem, 1fr)"
			data={data}
			columns={columns}
			sortFns={sortFns}
			entity="revisions"
			actions={displayActions ? ["edit", "delete"] : []}
			defaultPageSize={defaultPageSize}
			setEditing={setEditing}
		/>
	);
}
